import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { PageContent } from '../components/Layout'
import { API_BASE_URL } from '../utils/api'

interface BillingUser {
  id: number
  email: string
  name: string
  first_name: string
  last_name: string
  created_at: string
  
  // Subscription
  stripe_customer_id: string | null
  stripe_subscription_id: string | null
  subscription_status: string | null
  subscription_current_period_end: string | null
  subscription_canceled_at: string | null
  
  // Trial
  trial_started_at: string | null
  trial_ends_at: string | null
  trial_used: boolean
  is_trial_active: boolean
  is_trial_expired: boolean
  trial_days_remaining: number
  
  // Tier
  tier_id: number | null
  tier_name: string
  tier_price: number
  is_trial_tier: boolean
  
  // Computed
  has_paid_subscription: boolean
  status_label: string
  status_color: string
}

const AdminBillingManagement: React.FC = () => {
  const { t } = useTranslation()
  const [users, setUsers] = useState<BillingUser[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [tierFilter, setTierFilter] = useState('')
  const [selectedUser, setSelectedUser] = useState<BillingUser | null>(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [actionLoading, setActionLoading] = useState<number | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    fetchUsers()
  }, [search, statusFilter, tierFilter])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      const params = new URLSearchParams()
      if (search) params.append('search', search)
      if (statusFilter) params.append('status', statusFilter)
      if (tierFilter) params.append('tier', tierFilter)

      const response = await fetch(`${API_BASE_URL}/api/admin/billing/users?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setUsers(data.users)
      } else {
        setErrorMessage('Failed to load users')
      }
    } catch (error) {
      console.error('Error fetching users:', error)
      setErrorMessage('Error loading users')
    } finally {
      setLoading(false)
    }
  }

  const handleSyncUser = async (userId: number) => {
    try {
      setActionLoading(userId)
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/admin/sync-user-subscription/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setSuccessMessage(data.message)
        await fetchUsers()
      } else {
        const data = await response.json()
        setErrorMessage(data.message || 'Failed to sync user')
      }
    } catch (error) {
      console.error('Error syncing user:', error)
      setErrorMessage('Error syncing user subscription')
    } finally {
      setActionLoading(null)
      setTimeout(() => setSuccessMessage(null), 3000)
      setTimeout(() => setErrorMessage(null), 5000)
    }
  }

  const handleClearAllCaches = async () => {
    if (!confirm('Clear all tier caches? This will force all users to reload subscription data.')) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/admin/clear-all-caches`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        setSuccessMessage('All caches cleared successfully')
      } else {
        setErrorMessage('Failed to clear caches')
      }
    } catch (error) {
      console.error('Error clearing caches:', error)
      setErrorMessage('Error clearing caches')
    } finally {
      setTimeout(() => setSuccessMessage(null), 3000)
      setTimeout(() => setErrorMessage(null), 5000)
    }
  }

  const getStatusBadgeClasses = (color: string) => {
    const baseClasses = 'px-3 py-1 rounded-full text-xs font-medium'
    switch (color) {
      case 'green':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`
      case 'blue':
        return `${baseClasses} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`
      case 'yellow':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`
      case 'orange':
        return `${baseClasses} bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200`
      case 'red':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200`
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    })
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount)
  }

  return (
    <PageContent>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Billing Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Manage user subscriptions, trials, and billing
            </p>
          </div>
          <button
            onClick={handleClearAllCaches}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            🔄 Clear All Caches
          </button>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <p className="text-green-800 dark:text-green-200">{successMessage}</p>
          </div>
        )}
        {errorMessage && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200">{errorMessage}</p>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Search
              </label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name or email..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="trialing">Trialing</option>
                <option value="past_due">Past Due</option>
                <option value="canceled">Canceled</option>
                <option value="no_subscription">No Subscription</option>
              </select>
            </div>

            {/* Tier Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tier
              </label>
              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Tiers</option>
                <option value="none">No Tier</option>
                {/* Add more tier options dynamically if needed */}
              </select>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              Loading users...
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              No users found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Tier
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Trial
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Subscription
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {user.email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadgeClasses(user.status_color)}>
                          {user.status_label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {user.tier_name}
                        </div>
                        {user.tier_price > 0 && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {formatCurrency(user.tier_price)}/mo
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.is_trial_active ? (
                          <div className="text-sm">
                            <div className="text-green-600 dark:text-green-400 font-medium">
                              {user.trial_days_remaining} days left
                            </div>
                            <div className="text-gray-500 dark:text-gray-400">
                              Ends {formatDate(user.trial_ends_at)}
                            </div>
                          </div>
                        ) : user.trial_used ? (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {user.is_trial_expired ? 'Expired' : 'Used'}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Not started
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.stripe_subscription_id ? (
                          <div className="text-sm">
                            <div className="text-gray-900 dark:text-white font-mono text-xs">
                              {user.stripe_subscription_id.substring(0, 20)}...
                            </div>
                            {user.subscription_current_period_end && (
                              <div className="text-gray-500 dark:text-gray-400">
                                Renews {formatDate(user.subscription_current_period_end)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            No subscription
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedUser(user)
                              setShowDetailsModal(true)
                            }}
                            className="text-gray-900 hover:text-blue-800 dark:text-gray-600 dark:hover:text-blue-300"
                            title="View Details"
                          >
                            👁️
                          </button>
                          <button
                            onClick={() => handleSyncUser(user.id)}
                            disabled={actionLoading === user.id}
                            className="text-gray-900 hover:text-purple-800 dark:text-gray-600 dark:hover:text-purple-300 disabled:opacity-50"
                            title="Sync from Stripe"
                          >
                            {actionLoading === user.id ? '⏳' : '🔄'}
                          </button>
                          {user.stripe_customer_id && (
                            <a
                              href={`https://dashboard.stripe.com/customers/${user.stripe_customer_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                              title="View in Stripe"
                            >
                              💳
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {users.length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Total Users</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {users.filter(u => u.has_paid_subscription).length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Active Subscriptions</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {users.filter(u => u.is_trial_active).length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Active Trials</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
              {users.filter(u => !u.has_paid_subscription && !u.is_trial_active).length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Inactive</div>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  User Details
                </h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                {/* User Info */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">User Information</h3>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded p-3 space-y-1 text-sm">
                    <div><span className="font-medium">Name:</span> {selectedUser.name}</div>
                    <div><span className="font-medium">Email:</span> {selectedUser.email}</div>
                    <div><span className="font-medium">User ID:</span> {selectedUser.id}</div>
                    <div><span className="font-medium">Created:</span> {formatDate(selectedUser.created_at)}</div>
                  </div>
                </div>

                {/* Subscription Info */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Subscription</h3>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded p-3 space-y-1 text-sm">
                    <div><span className="font-medium">Status:</span> <span className={getStatusBadgeClasses(selectedUser.status_color)}>{selectedUser.status_label}</span></div>
                    <div><span className="font-medium">Stripe Customer:</span> {selectedUser.stripe_customer_id || 'None'}</div>
                    <div><span className="font-medium">Stripe Subscription:</span> {selectedUser.stripe_subscription_id || 'None'}</div>
                    <div><span className="font-medium">Period Ends:</span> {formatDate(selectedUser.subscription_current_period_end)}</div>
                    {selectedUser.subscription_canceled_at && (
                      <div><span className="font-medium">Canceled At:</span> {formatDate(selectedUser.subscription_canceled_at)}</div>
                    )}
                  </div>
                </div>

                {/* Trial Info */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Trial</h3>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded p-3 space-y-1 text-sm">
                    <div><span className="font-medium">Trial Used:</span> {selectedUser.trial_used ? 'Yes' : 'No'}</div>
                    <div><span className="font-medium">Started:</span> {formatDate(selectedUser.trial_started_at)}</div>
                    <div><span className="font-medium">Ends:</span> {formatDate(selectedUser.trial_ends_at)}</div>
                    <div><span className="font-medium">Days Remaining:</span> {selectedUser.trial_days_remaining}</div>
                    <div><span className="font-medium">Active:</span> {selectedUser.is_trial_active ? '✅ Yes' : '❌ No'}</div>
                    <div><span className="font-medium">Expired:</span> {selectedUser.is_trial_expired ? '✅ Yes' : '❌ No'}</div>
                  </div>
                </div>

                {/* Tier Info */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Tier</h3>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded p-3 space-y-1 text-sm">
                    <div><span className="font-medium">Tier:</span> {selectedUser.tier_name}</div>
                    <div><span className="font-medium">Price:</span> {formatCurrency(selectedUser.tier_price)}/month</div>
                    <div><span className="font-medium">Tier ID:</span> {selectedUser.tier_id || 'None'}</div>
                    <div><span className="font-medium">Is Trial Tier:</span> {selectedUser.is_trial_tier ? 'Yes' : 'No'}</div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleSyncUser(selectedUser.id)
                    setShowDetailsModal(false)
                  }}
                  className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
                >
                  Sync from Stripe
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageContent>
  )
}

export default AdminBillingManagement

